import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

export type DocumentType = 'factura' | 'cotizacion' | 'orden_compra' | 'nota_credito';
export type DocumentStatus = 'draft' | 'approved' | 'converted';

interface Factura {
  id: string;
  ncf: string;
  client_id: string;
  cliente: string;
  rnc_cedula: string;
  telefono?: string;
  correo?: string;
  direccion?: string;
  fecha: string;
  vencimiento: string;
  lineas: Array<{
    pantalla: string;
    precio: number;
    cantidad_dias: number;
    descuento: number;
    impuesto: number;
    total: number;
    bonificada?: boolean;
  }>;
  terminos_condiciones?: string;
  notas?: string;
  vendedor?: {
    full_name?: string;
    email?: string;
    raw_user_meta_data?: {
      full_name: string;
    };
    role?: string;
  };
  logo_url?: string;
  total?: number;
  subtotal?: number;
  descuentos?: number;
  impuestos?: number;
  document_type?: DocumentType;
  status?: DocumentStatus;
}

// Estilos para el PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 35,
    borderBottom: 2,
    borderBottomColor: '#0F2E3A',
    paddingBottom: 30,
    minHeight: 130,
  },
  logoContainer: {
    width: 150,
    height: 80,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    backgroundColor: '#FFFFFF',
    marginBottom: 8,
  },
  logo: {
    maxWidth: 150,
    maxHeight: 80,
    width: 'auto',
    height: 'auto',
  },
  companyInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0F2E3A',
    marginBottom: 5,
  },
  companyDetails: {
    fontSize: 10,
    color: '#6D6D6D',
    lineHeight: 1.4,
  },
  invoiceTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#0F2E3A',
    textAlign: 'right',
    marginBottom: 10,
  },
  invoiceNumber: {
    fontSize: 12,
    color: '#6D6D6D',
    textAlign: 'right',
  },
  draftStatus: {
    fontSize: 10,
    color: '#E74C3C',
    textAlign: 'right',
    fontWeight: 'bold',
    marginTop: 5,
    backgroundColor: '#FCE4E6',
    padding: 3,
  },
  clientSection: {
    marginBottom: 30,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0F2E3A',
    marginBottom: 10,
    backgroundColor: '#DCE6EB',
    padding: 8,
  },
  clientInfo: {
    fontSize: 11,
    lineHeight: 1.5,
    color: '#333333',
  },
  table: {
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#DCE6EB',
    marginBottom: 30,
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row',
  },
  tableHeader: {
    backgroundColor: '#0F2E3A',
  },
  tableHeaderCell: {
    width: '20%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#DCE6EB',
    backgroundColor: '#0F2E3A',
    padding: 8,
  },
  tableHeaderText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  tableCell: {
    width: '20%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#DCE6EB',
    padding: 8,
  },
  tableCellText: {
    fontSize: 9,
    color: '#333333',
    textAlign: 'center',
  },
  tableCellTextFree: {
    fontSize: 9,
    color: '#27AE60',
    textAlign: 'center',
    fontWeight: 'bold',
    marginTop: 2,
    lineHeight: 1.2,
  },
  totalSection: {
    alignItems: 'flex-end',
    marginTop: 20,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 5,
    width: '50%',
  },
  totalLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#0F2E3A',
    width: 100,
    textAlign: 'right',
    marginRight: 20,
  },
  totalValue: {
    fontSize: 12,
    color: '#333333',
    width: 100,
    textAlign: 'right',
  },
  grandTotal: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    backgroundColor: '#FEC940',
    padding: 10,
    width: '50%',
    marginTop: 10,
  },
  grandTotalLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0F2E3A',
    width: 100,
    textAlign: 'right',
    marginRight: 20,
  },
  grandTotalValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0F2E3A',
    width: 100,
    textAlign: 'right',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 10,
    color: '#6D6D6D',
    borderTop: 1,
    borderTopColor: '#DCE6EB',
    paddingTop: 10,
  },
  dateSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    fontSize: 11,
    color: '#333333',
  },
});

interface FacturaPDFTemplateProps {
  factura: Factura;
  logoUrl?: string;
  screens: Array<{
    id: string;
    name: string;
    location: string;
  }>;
  companyConfig?: {
    company_name: string;
    company_address?: string;
    company_city?: string;
    company_state?: string;
    company_country?: string;
    company_email?: string;
    company_website?: string;
    company_phone?: string;
    company_logo_url?: string;
    rnc?: string;
  };
}

const FacturaPDFTemplate: React.FC<FacturaPDFTemplateProps> = ({ factura, logoUrl: defaultLogoUrl, screens, companyConfig }) => {
  // Función auxiliar para obtener la información de la pantalla
  const getPantallaInfo = (pantallaId: string) => {
    const screen = screens?.find(s => s.id === pantallaId);
    if (screen) {
      return `${screen.name} - ${screen.location}`;
    }
    return 'Pantalla no encontrada';
  };

  // Función para obtener el título del documento según el tipo
  const getDocumentTitle = (documentType?: DocumentType): string => {
    const titles = {
      'factura': 'FACTURA',
      'cotizacion': 'COTIZACIÓN',
      'orden_compra': 'ORDEN DE COMPRA',
      'nota_credito': 'NOTA DE CRÉDITO'
    };
    return titles[documentType || 'factura'];
  };

  // Calcular valores localmente ya que no están en la BD
  // Filtrar solo líneas NO bonificadas para los cálculos (igual que en el formulario)
  const lineasNoBonificadas = factura.lineas.filter(linea => !linea.bonificada);
  
  const subtotalBruto = lineasNoBonificadas.reduce((acc, linea) => 
    acc + (linea.precio * linea.cantidad_dias), 0
  );
  
  const descuentosTotal = lineasNoBonificadas.reduce((acc, linea) => 
    acc + ((linea.precio * linea.cantidad_dias) * (linea.descuento / 100)), 0
  );
  
  const subtotal = subtotalBruto - descuentosTotal;
  const itbis = subtotal * 0.18;

  // Prioridad de logos: 1) Logo de la empresa (company_config), 2) Logo específico de la factura, 3) Logo por defecto
  const logoToUse = companyConfig?.company_logo_url || factura.logo_url || defaultLogoUrl;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            {logoToUse ? (
              <View style={styles.logoContainer}>
                <Image style={styles.logo} src={logoToUse} />
              </View>
            ) : (
              <Text style={styles.companyName}>{companyConfig?.company_name || 'GestorAD'}</Text>
            )}
            <Text style={styles.companyDetails}>
              {companyConfig?.company_address && `${companyConfig.company_address}\n`}
              {companyConfig?.company_city && companyConfig?.company_state 
                ? `${companyConfig.company_city}, ${companyConfig.company_state}\n`
                : companyConfig?.company_city 
                  ? `${companyConfig.company_city}\n`
                  : ''
              }
              {companyConfig?.company_country || 'República Dominicana'}{'\n'}
              {companyConfig?.company_phone 
                ? `Tel: ${companyConfig.company_phone}`
                : 'Tel: (809) 000-0000'}{'\n'}
              {companyConfig?.company_email 
                ? `Email: ${companyConfig.company_email}`
                : 'Email: info@gestorad.com'}
            </Text>
          </View>
          <View>
            <Text style={styles.invoiceTitle}>{getDocumentTitle(factura.document_type)}</Text>
            <Text style={styles.invoiceNumber}>NCF: {factura.ncf}</Text>
            <Text style={styles.invoiceNumber}>RNC: {companyConfig?.rnc || factura.rnc_cedula}</Text>
            {factura.status === 'draft' && (
              <Text style={styles.draftStatus}>BORRADOR</Text>
            )}
          </View>
        </View>

        {/* Información de fechas */}
        <View style={styles.dateSection}>
          <Text>Fecha de Emisión: {new Date(factura.fecha).toLocaleDateString('es-DO')}</Text>
          <Text>Fecha de Vencimiento: {new Date(factura.vencimiento).toLocaleDateString('es-DO')}</Text>
        </View>

        {/* Información del cliente */}
        <View style={styles.clientSection}>
          <Text style={styles.sectionTitle}>DATOS DEL CLIENTE</Text>
          <Text style={styles.clientInfo}>
            Cliente: {factura.cliente}{'\n'}
            RNC/Cédula: {factura.rnc_cedula}{'\n'}
            {factura.telefono && `Teléfono: ${factura.telefono}\n`}
            {factura.correo && `Correo: ${factura.correo}\n`}
            {factura.direccion && `Dirección: ${factura.direccion}\n`}
            {factura.vendedor?.role === 'mantenimiento' ? 'Encargado de Mantenimiento' : 'Vendedor'}: {factura.vendedor?.raw_user_meta_data?.full_name || factura.vendedor?.full_name || factura.vendedor?.email || 'Sistema GestorAD'}
          </Text>
        </View>

        {/* Tabla de líneas de facturación */}
        <View style={styles.table}>
          {/* Header de la tabla */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <View style={[styles.tableHeaderCell, { width: '35%' }]}>
              <Text style={styles.tableHeaderText}>Pantalla</Text>
            </View>
            <View style={[styles.tableHeaderCell, { width: '15%' }]}>
              <Text style={styles.tableHeaderText}>Precio</Text>
            </View>
            <View style={[styles.tableHeaderCell, { width: '15%' }]}>
              <Text style={styles.tableHeaderText}>Días</Text>
            </View>
            <View style={[styles.tableHeaderCell, { width: '15%' }]}>
              <Text style={styles.tableHeaderText}>Desc. %</Text>
            </View>
            <View style={[styles.tableHeaderCell, { width: '10%' }]}>
              <Text style={styles.tableHeaderText}>Imp. %</Text>
            </View>
            <View style={[styles.tableHeaderCell, { width: '10%' }]}>
              <Text style={styles.tableHeaderText}>Total</Text>
            </View>
          </View>

          {/* Filas de datos */}
          {factura.lineas.map((linea, index) => (
            <View style={styles.tableRow} key={index}>
              <View style={[styles.tableCell, { width: '35%' }]}>
                <Text style={styles.tableCellText}>
                  {getPantallaInfo(linea.pantalla)}
                  {linea.bonificada && (
                    <Text style={styles.tableCellTextFree}>{'\n'}BONIFICADA</Text>
                  )}
                </Text>
              </View>
              <View style={[styles.tableCell, { width: '15%' }]}>
                <Text style={linea.bonificada ? styles.tableCellTextFree : styles.tableCellText}>
                  RD$ {new Intl.NumberFormat('es-DO').format(linea.precio)}
                </Text>
              </View>
              <View style={[styles.tableCell, { width: '15%' }]}>
                <Text style={styles.tableCellText}>
                  {linea.cantidad_dias}
                </Text>
              </View>
              <View style={[styles.tableCell, { width: '15%' }]}>
                <Text style={linea.bonificada ? styles.tableCellTextFree : styles.tableCellText}>
                  {linea.bonificada ? 'N/A' : `${linea.descuento || 0}%`}
                </Text>
              </View>
              <View style={[styles.tableCell, { width: '10%' }]}>
                <Text style={linea.bonificada ? styles.tableCellTextFree : styles.tableCellText}>
                  {linea.bonificada ? 'N/A' : `${linea.impuesto || 0}%`}
                </Text>
              </View>
              <View style={[styles.tableCell, { width: '10%' }]}>
                <Text style={linea.bonificada ? styles.tableCellTextFree : styles.tableCellText}>
                  RD$ {new Intl.NumberFormat('es-DO').format(linea.bonificada ? (linea.precio * linea.cantidad_dias) : linea.total)}
                </Text>
              </View>
            </View>
          ))}
        </View>

        {/* Sección de totales */}
        <View style={styles.totalSection}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Subtotal Bruto:</Text>
            <Text style={styles.totalValue}>RD$ {new Intl.NumberFormat('es-DO').format(subtotalBruto)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Descuentos:</Text>
            <Text style={styles.totalValue}>RD$ {new Intl.NumberFormat('es-DO').format(descuentosTotal)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Subtotal:</Text>
            <Text style={styles.totalValue}>RD$ {new Intl.NumberFormat('es-DO').format(subtotal)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>ITBIS (18%):</Text>
            <Text style={styles.totalValue}>RD$ {new Intl.NumberFormat('es-DO').format(itbis)}</Text>
          </View>
          <View style={styles.grandTotal}>
            <Text style={styles.grandTotalLabel}>TOTAL:</Text>
            <Text style={styles.grandTotalValue}>RD$ {new Intl.NumberFormat('es-DO').format(factura.total)}</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>
            Factura generada automáticamente por {companyConfig?.company_name || 'GestorAD'} - Sistema de Gestión Publicitaria{'\n'}
            Para consultas: {companyConfig?.company_email || 'info@gestorad.com'} | Tel: {companyConfig?.company_phone || '(809) 000-0000'}
            {companyConfig?.company_website && ` | Web: ${companyConfig.company_website}`}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default FacturaPDFTemplate; 